class PanelConfigurator extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.currentVariantId = null;
    this.elements = {};
    this.quantity = 1;
    this._adding = false;
  }

  connectedCallback() {
    this.loadVariants();
    this.cacheElements();
    this.currentVariantId = this.detectCurrentVariantId();
    this.bindEvents();
    this.handleInitialExpansion();
    this.setQuantity(1);
    this.prefillFromUrl();
  }

  loadVariants() {
    const dataEl = this.querySelector('script[data-variants]');
    if (dataEl) {
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[panel-configurator] failed to parse variants', e);
        this.variants = [];
      }
    }
    // Lengtes als los product (NL: 240 en 300 cm zijn maat-siblings, geen varianten).
    const siblingsEl = this.querySelector('script[data-size-siblings]');
    this.sizeSiblings = [];
    if (siblingsEl) {
      try { this.sizeSiblings = JSON.parse(siblingsEl.textContent); } catch (e) { this.sizeSiblings = []; }
    }
  }

  cacheElements() {
    this.elements = {
      toggle: this.querySelector('[data-toggle]'),
      body: this.querySelector('[data-body]'),
      heightInput: this.querySelector('[data-input="height"]'),
      widthInput: this.querySelector('[data-input="width"]'),
      recommendation: this.querySelector('[data-recommendation]'),
      recommendationText: this.querySelector('[data-recommendation-text]'),
      recommendationAction: this.querySelector('[data-recommendation-action]'),
      accessories: Array.from(this.querySelectorAll('[data-accessory]')).map((row) => ({
        checkbox: row.querySelector('[data-accessory-checkbox]'),
        qtyEl: row.querySelector('[data-accessory-qty]'),
        variantId: parseInt(row.dataset.variantId, 10),
        perPanels: parseInt(row.dataset.perPanels, 10) || 0,
        price: parseInt(row.dataset.price, 10) || 0,
      })),
      coverage: this.querySelector('[data-coverage]'),
      qtyInput: this.querySelector('[data-qty-input]'),
      qtyDecrease: this.querySelector('[data-qty-decrease]'),
      qtyIncrease: this.querySelector('[data-qty-increase]'),
      atc: this.querySelector('[data-atc]'),
      checkout: this.querySelector('[data-checkout]'),
      atcTotal: this.querySelector('[data-atc-total]'),
      atcText: this.querySelector('[data-atc-text]'),
      atcPrice: this.querySelector('[data-atc-price]'),
      atcError: this.querySelector('[data-atc-error]'),
      klarnaAmount: this.querySelector('[data-klarna-amount]'),
    };
  }

  showError(msg) {
    if (!this.elements.atcError) return;
    this.elements.atcError.textContent = msg || '';
    this.elements.atcError.hidden = !msg;
  }

  // 'grid' (rows x columns of rectangular panels), 'hexagon' (honeycomb, area based) or
  // 'area' (wall area / sheet area: mosaic sheets that interlock and are cut to size).
  get calcMode() {
    const mode = this.dataset.calcMode;
    return (mode === 'hexagon' || mode === 'area') ? mode : 'grid';
  }

  // Wall area one panel really covers, in cm². A hexagon's bounding box (width x height)
  // is 25% larger than the hexagon itself, and in a honeycomb the hexagons interlock,
  // so the true area is 0.75 x width x height.
  panelAreaCm2(variant) {
    const panelW = this.variantDimToCm(variant.width, variant.width_unit);
    const panelH = this.variantDimToCm(variant.height, variant.height_unit);
    if (panelW === 0 || panelH === 0) return 0;
    return panelW * panelH * (this.calcMode === 'hexagon' ? 0.75 : 1);
  }

  detectCurrentVariantId() {
    const input = document.querySelector('[name="id"]');
    if (input && input.value) return parseInt(input.value, 10);
    return this.variants[0] ? this.variants[0].id : null;
  }

  getCurrentVariant() {
    return this.variants.find(v => v.id === this.currentVariantId) || this.variants[0];
  }

  bindEvents() {
    if (this.elements.toggle) {
      this.elements.toggle.addEventListener('click', () => this.toggleBody());
    }
    if (this.elements.heightInput) {
      this.elements.heightInput.addEventListener('input', () => this.recalc());
    }
    if (this.elements.widthInput) {
      this.elements.widthInput.addEventListener('input', () => this.recalc());
    }
    if (this.elements.qtyDecrease) {
      this.elements.qtyDecrease.addEventListener('click', () => this.setQuantity(this.getQuantity() - 1));
    }
    if (this.elements.qtyIncrease) {
      this.elements.qtyIncrease.addEventListener('click', () => this.setQuantity(this.getQuantity() + 1));
    }
    if (this.elements.qtyInput) {
      this.elements.qtyInput.addEventListener('change', () => this.setQuantity(this.getQuantity()));
    }
    this.elements.accessories.forEach((acc) => {
      if (acc.checkbox) acc.checkbox.addEventListener('change', () => this.updateSummary());
    });
    if (this.elements.atc) {
      this.elements.atc.addEventListener('click', (e) => this.addToCart(e.currentTarget));
    }
    if (this.elements.checkout) {
      this.elements.checkout.addEventListener('click', (e) => this.addToCart(e.currentTarget, { checkout: true }));
    }
    // Het thema stuurt 'variant:change' naar het productformulier zonder bubbles, dus dat komt niet
    // op document aan. De pubsub-melding wel; varianten van een ander product (quick view) negeren.
    const onVariantChange = (v) => {
      if (v && !this.variants.some(x => x.id === v.id)) return;
      this.currentVariantId = v ? v.id : this.detectCurrentVariantId();
      this.recalc();
      this.updateSummary();
    };
    document.addEventListener('variant:change', (e) => onVariantChange(e && e.detail && e.detail.variant));
    if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      this.variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        onVariantChange(event && event.data && event.data.variant);
      });
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
  }

  handleInitialExpansion() {
    const expanded = this.dataset.expanded === 'true';
    if (this.elements.body) this.elements.body.hidden = !expanded;
    if (this.elements.toggle) this.elements.toggle.setAttribute('aria-expanded', String(expanded));
  }

  toggleBody(force) {
    if (!this.elements.body) return;
    const open = force !== undefined ? force : this.elements.body.hidden;
    this.elements.body.hidden = !open;
    if (this.elements.toggle) this.elements.toggle.setAttribute('aria-expanded', String(open));
  }

  parseInput(input) {
    if (!input) return 0;
    const value = parseFloat(input.value);
    if (isNaN(value) || value <= 0) return 0;
    return value;
  }

  // Normalize a variant dimension (value + Shopify unit enum) to cm.
  variantDimToCm(value, unit) {
    if (!value || value <= 0) return 0;
    // Liquid gives the short unit ('cm', 'in', 'mm', 'm'); the Admin API the enum ('INCHES').
    const u = String(unit || 'cm').toLowerCase();
    if (u === 'mm' || u === 'millimeters') return value / 10;
    if (u === 'm' || u === 'meters') return value * 100;
    if (u === 'in' || u === 'inches') return value * 2.54;
    if (u === 'ft' || u === 'feet') return value * 30.48;
    return value; // cm or unknown -> assume cm
  }

  // Convert a user input value (cm or inch per theme setting) to cm.
  inputToCm(value) {
    const unit = this.dataset.defaultUnit || 'cm';
    return unit === 'inch' ? value * 2.54 : value;
  }

  recalc() {
    const wallH = this.inputToCm(this.parseInput(this.elements.heightInput));
    const wallW = this.inputToCm(this.parseInput(this.elements.widthInput));

    this.updateRecommendation(wallH, wallW);

    if (wallH === 0 || wallW === 0) return; // keep current quantity until both are set

    const variant = this.getCurrentVariant();
    if (!variant) return;

    if (this.calcMode === 'hexagon' || this.calcMode === 'area') {
      const area = this.panelAreaCm2(variant);
      if (area === 0) return;
      // Kleine marge tegen afrondingsruis (bijv. 0,09 / 0,0858 = 1,0489...).
      this.setQuantity(Math.ceil((wallW * wallH) / area - 1e-9));
      return;
    }

    const panelW = this.variantDimToCm(variant.width, variant.width_unit);
    const panelH = this.variantDimToCm(variant.height, variant.height_unit);
    if (panelW === 0 || panelH === 0) return;

    this.setQuantity(this.panelsNeeded(wallW, wallH, panelW, panelH));
  }

  // Per kolom zoveel hele panelen als in de hoogte passen. Het restant (of de hele hoogte als die
  // korter is dan een paneel) wordt op maat gezaagd, en uit één paneel gaan meerdere van die
  // stukken: bij 310 cm hoog en panelen van 300 cm komen de stukken van 10 cm voor alle kolommen
  // uit één extra paneel, in plaats van een tweede rij hele panelen.
  // Patroontegels (deco, 3D) gaan heel op de wand: dan gewoon rijen x kolommen.
  panelsNeeded(wallW, wallH, panelW, panelH) {
    // Zaagsnede telt niet mee: 240 cm geeft 2 stukken van 120, zoals een klant het ook rekent.
    const cols = Math.ceil(wallW / panelW - 1e-9);
    if (this.dataset.reuseOffcuts === 'false') return Math.max(1, cols * Math.ceil(wallH / panelH - 1e-9));
    const fullPerCol = Math.floor(wallH / panelH + 1e-9);
    const rest = wallH - fullPerCol * panelH;
    let qty = cols * fullPerCol;
    if (rest > 0.05) {
      const piecesPerPanel = Math.max(1, Math.floor(panelH / rest + 1e-9));
      qty += Math.ceil(cols / piecesPerPanel);
    }
    return Math.max(1, qty);
  }

  updateRecommendation(wallHcm, wallWcm) {
    const el = this.elements.recommendation;
    if (!el) return;
    const hide = () => {
      el.hidden = true;
      if (this.elements.recommendationAction) this.elements.recommendationAction.hidden = true;
    };

    // Standard lengths only exist for rectangular panels.
    if (this.calcMode !== 'grid') { hide(); return; }

    const variant = this.getCurrentVariant();
    const currentLen = variant ? this.variantDimToCm(variant.height, variant.height_unit) : 0;
    const panelW = variant ? this.variantDimToCm(variant.width, variant.width_unit) : 0;
    if (!wallHcm || wallHcm <= 0 || !currentLen || !panelW) { hide(); return; }

    // An empty attribute means the product has none of the standard lengths as a variant
    // or size sibling (deco, mosaic): no recommendation then. Only a missing attribute falls back.
    const lengths = (this.dataset.standardLengths ?? '240,300')
      .split(',').map(s => parseFloat(s.trim())).filter(n => n > 0);
    const isCurrent = (len) => Math.abs(len - currentLen) < 0.5;
    if (!lengths.some(isCurrent) || !lengths.some(len => !isCurrent(len))) { hide(); return; }

    // Elke lengte doorrekenen en de beste kiezen: minste panelen, dan minste naden per kolom
    // (250 cm: liever 300 zagen dan 240 + een stuk erbij), dan een naad vlak onder het plafond
    // (310 cm: 300 + strook van 10 cm valt weg in de hoek, 240 + 70 cm niet), dan minste
    // zaagafval (480 cm: 2x 240 in plaats van 300 + 180), bij gelijkspel de huidige lengte.
    // Zonder breedte: één kolom.
    const CEILING_STRIP_CM = 30;
    const wallW = wallWcm > 0 ? wallWcm : panelW;
    const options = lengths.map((len) => {
      const qty = this.panelsNeeded(wallW, wallHcm, panelW, len);
      const full = Math.floor(wallHcm / len + 1e-9);
      const strip = wallHcm - full * len;
      return {
        len,
        qty,
        joints: Math.max(0, Math.ceil(wallHcm / len - 1e-9) - 1),
        seamAtCeiling: full > 0 && strip > 0.05 && strip <= CEILING_STRIP_CM,
        material: qty * len
      };
    });
    options.sort((a, b) => a.qty - b.qty || a.joints - b.joints
      || (b.seamAtCeiling - a.seamAtCeiling) || a.material - b.material
      || (isCurrent(a.len) ? -1 : isCurrent(b.len) ? 1 : 0));
    const best = options[0];
    const rec = best.len;
    if (isCurrent(rec)) { hide(); return; }

    // Waarom deze lengte, zodat een lager aantal na de knop niet als fout overkomt. Het aantal
    // alleen met ingevulde breedte; zonder breedte is het één kolom en klopt het niet voor de wand.
    const current = options.find((option) => isCurrent(option.len));
    const t = (key, fallback) => (window.AKU_I18N && window.AKU_I18N[key]) || fallback;
    let reason = '';
    if (wallWcm > 0 && best.qty < current.qty) {
      reason = t('reason_fewer', '%count% instead of %current% panels').replace('%count%', best.qty).replace('%current%', current.qty);
    } else if (best.joints < current.joints) {
      reason = best.joints === 0 ? t('reason_no_seam', 'without a seam') : t('reason_fewer_seams', 'fewer seams');
    } else if (best.seamAtCeiling && !current.seamAtCeiling) {
      reason = t('reason_seam_top', 'seam just below the ceiling');
    } else if (best.material < current.material) {
      reason = t('reason_less_waste', 'less offcut waste');
    }

    const tpl = t('recommend', 'Based on the specified height, we recommend panels of %length% cm');
    // USA rekent in inches: de lengte dan ook zo tonen (118.1" in plaats van 300 cm).
    const imperial = (this.dataset.defaultUnit || 'cm') === 'inch';
    const lengthLabel = imperial ? `${(rec / 2.54).toFixed(1)}"` : `${rec} cm`;
    if (this.elements.recommendationText) {
      this.elements.recommendationText.textContent = tpl.replace(/%length%( cm)?/, lengthLabel) + (reason ? ` (${reason})` : '');
    }
    this.setRecommendationAction(rec);
    el.hidden = false;
  }

  // Maakt het advies actiegericht: een variant met die lengte wordt met een klik gekozen (UK),
  // een los product met die lengte (NL-maat-sibling) wordt geopend met de wandmaten ingevuld.
  setRecommendationAction(lengthCm) {
    const btn = this.elements.recommendationAction;
    if (!btn) return;
    const sameLen = (h, unit) => Math.abs(this.variantDimToCm(h, unit) - lengthCm) < 0.5;
    const chooseTpl = (window.AKU_I18N && window.AKU_I18N.choose_size) || 'Choose %size%';

    const variant = this.variants.find(v => v.available !== false && sameLen(v.height, v.height_unit));
    // FR zet 3- en 1-zijdig in één maatgroep ('300 cm x 60 cm (Placage sur 1 côté)'): bij gelijke lengte
    // de sibling met dezelfde afwerking kiezen, het deel tussen haakjes (zoals de maatkiezer).
    const finish = (label) => (String(label || '').match(/\(([^)]*)\)\s*$/) || [])[1] || '';
    const ownFinish = finish((this.sizeSiblings.find(s => s.current) || {}).label);
    const candidates = variant ? [] : this.sizeSiblings.filter(s => !s.current && sameLen(s.height, s.height_unit));
    const sibling = candidates.find(s => finish(s.label) === ownFinish) || candidates[0];
    if (!variant && !sibling) { btn.hidden = true; btn.onclick = null; return; }

    btn.textContent = chooseTpl.replace('%size%', variant ? variant.title : (sibling.label || lengthCm + ' cm'));
    btn.hidden = false;
    btn.onclick = () => {
      if (variant) {
        this.selectVariant(variant);
      } else {
        const url = new URL(sibling.url, window.location.origin);
        const h = this.parseInput(this.elements.heightInput);
        const w = this.parseInput(this.elements.widthInput);
        if (h) url.searchParams.set('calc_h', h);
        if (w) url.searchParams.set('calc_w', w);
        window.location.href = url.toString();
      }
    };
  }

  // Kiest een variant via de variantkiezer van de pagina, zodat prijs, media en formulier meelopen.
  selectVariant(variant) {
    const form = this.closest('section, .shopify-section') || document;
    const options = String(variant.title || '').split(' / ');
    let clicked = false;
    options.forEach((value) => {
      const input = Array.from(form.querySelectorAll('variant-picker input[type="radio"], .variant-picker input[type="radio"]'))
        .find(i => i.value === value && !i.checked);
      if (input) { input.click(); clicked = true; }
    });
    if (!clicked) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.location.href = url.toString();
    }
  }

  // Komt de bezoeker via de adviesknop van een andere lengte, dan staan de wandmaten in de URL.
  prefillFromUrl() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    const h = parseFloat(params.get('calc_h'));
    const w = parseFloat(params.get('calc_w'));
    if (!(h > 0) && !(w > 0)) return;
    if (this.elements.heightInput && h > 0) this.elements.heightInput.value = h;
    if (this.elements.widthInput && w > 0) this.elements.widthInput.value = w;
    this.toggleBody(true);
    this.recalc();
  }

  getQuantity() {
    const v = parseInt(this.elements.qtyInput && this.elements.qtyInput.value, 10);
    return (isNaN(v) || v < 1) ? 1 : v;
  }

  setQuantity(n) {
    n = Math.max(1, Math.floor(n) || 1);
    this.quantity = n;
    if (this.elements.qtyInput) this.elements.qtyInput.value = n;
    this.updateSummary();
    // The sticky purchase bar (snippets/sticky_add_to_cart.liquid) mirrors this quantity.
    this.dispatchEvent(new CustomEvent('panel-configurator:quantity', { bubbles: true, detail: { quantity: n } }));
  }

  accessoryQty(panelQty, perPanels) {
    if (!perPanels || perPanels <= 0) return 1;
    return Math.max(1, Math.ceil(panelQty / perPanels));
  }

  updateSummary() {
    const variant = this.getCurrentVariant();
    const qty = this.getQuantity();

    // Uitverkocht (geen voorraad, verkopen zonder voorraad uit): knoppen uit en 'Uitverkocht', per gekozen variant.
    this.soldOut = !!variant && variant.available === false;
    [this.elements.atc, this.elements.checkout].forEach((btn) => btn && btn.toggleAttribute('disabled', this.soldOut || !!this._adding));
    if (this.elements.atc && this.elements.atcText) {
      this.elements.atcText.textContent = this.soldOut ? this.elements.atc.dataset.soldOut : this.elements.atc.dataset.label;
    }
    if (this.elements.atcPrice) this.elements.atcPrice.hidden = this.soldOut;

    if (this.elements.coverage && variant) {
      const imperial = (this.dataset.defaultUnit || 'cm') === 'inch';
      // cm² -> m², or -> sq ft when the store works in inches.
      const area = (this.panelAreaCm2(variant) * qty) / (imperial ? 929.0304 : 10000);
      let tpl = (window.AKU_I18N && window.AKU_I18N.coverage) || 'Covers %area% m²';
      if (imperial) tpl = tpl.replace('m²', 'sq ft');
      // Decimaalteken volgens de paginataal (NL/DE/FR: komma, EN: punt).
      let areaText = area.toFixed(2);
      try {
        areaText = area.toLocaleString(document.documentElement.lang || undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      } catch (e) {}
      this.elements.coverage.textContent = area > 0 ? tpl.replace('%area%', areaText) : '';
    }

    const accessoriesTotal = this.elements.accessories.reduce((sum, acc) => {
      if (!acc.checkbox || !acc.checkbox.checked) return sum;
      return sum + acc.price * this.accessoryQty(qty, acc.perPanels);
    }, 0);

    if (this.elements.atcTotal && variant) {
      this.elements.atcTotal.textContent = this.formatMoney(variant.price * qty + accessoriesTotal);
    }

    if (this.elements.klarnaAmount && variant) {
      const installments = parseInt(this.dataset.klarnaInstallments, 10) || 3;
      this.elements.klarnaAmount.textContent = this.formatMoney((variant.price * qty + accessoriesTotal) / installments);
    }

    this.elements.accessories.forEach((acc) => {
      if (acc.qtyEl) acc.qtyEl.textContent = this.accessoryQty(qty, acc.perPanels);
    });
  }

  formatMoney(cents) {
    const fmt = (window.AKU_I18N && window.AKU_I18N.money_format)
      || (window.theme && theme.settings && theme.settings.moneyFormat)
      || '€{{amount}}';
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, fmt);
    }
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  setBusy(busy, trigger) {
    [this.elements.atc, this.elements.checkout].forEach((btn) => {
      if (!btn) return;
      btn.toggleAttribute('disabled', busy || !!this.soldOut);
      btn.toggleAttribute('aria-busy', busy && (!trigger || btn === trigger));
    });
  }

  async addToCart(trigger, opts) {
    if (this._adding || this.soldOut) return;
    const toCheckout = !!(opts && opts.checkout);
    const variant = this.getCurrentVariant();
    if (!variant) return;

    const qty = this.getQuantity();
    const items = [{ id: variant.id, quantity: qty }];

    this.elements.accessories.forEach((acc) => {
      if (!acc.variantId || !acc.checkbox || !acc.checkbox.checked) return;
      items.push({ id: acc.variantId, quantity: this.accessoryQty(qty, acc.perPanels) });
    });

    const routes = (window.theme && theme.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url || '/cart.js';

    this._adding = true;
    this.setBusy(true, trigger);
    this.showError('');
    let redirecting = false;

    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));

      const config = (window.theme && theme.utils && theme.utils.fetchConfig)
        ? theme.utils.fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.method = 'POST';
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify({ items, sections, sections_url: window.location.pathname });

      const res = await fetch(addUrl, config);
      const parsed = await res.json();

      if (parsed.status) {
        // e.g. 422 when stock/quantity rules cap the request. Surface the message and
        // refresh the cart badge (Shopify may have partially added what was available).
        this.showError(parsed.description || parsed.message || '');
        try {
          const cj = await (await fetch(cartUrl, { method: 'GET', headers: { Accept: 'application/json' } })).json();
          if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'panel-configurator', cart: cj });
          }
        } catch (e) { /* non-fatal */ }
        return;
      }

      if (toCheckout) {
        redirecting = true;
        window.location.href = '/checkout';
        return;
      }

      let cartJson = null;
      try {
        cartJson = await (await fetch(cartUrl, { method: 'GET', headers: { Accept: 'application/json' } })).json();
      } catch (e) { /* non-fatal */ }
      if (cartJson) cartJson.sections = parsed.sections;

      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'panel-configurator',
          productVariantId: variant.id,
          cart: cartJson,
        });
      }
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') {
        drawer.show(trigger);
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      }
    } catch (e) {
      console.warn('[panel-configurator] add to cart failed', e);
    } finally {
      if (!redirecting) {
        this._adding = false;
        this.setBusy(false);
      }
    }
  }
}

if (!customElements.get('panel-configurator')) {
  customElements.define('panel-configurator', PanelConfigurator);
}
